/**
 * UserProjectDetailWithMandatoryUseCasesPage
 * [UC-057] Project detail view — mandatory + optional use cases, timeline,
 * change requests, incidents, development initiation, delivery download.
 * Mirrors specs/screens/user-project-detail-with-mandatory-use-cases.screen.md
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../core/api-client';
import { fmt, fmtMoney } from '../utils/format';
import { ExpandableMarkdown } from '../components/ui/ExpandableMarkdown';
import { Select } from '../components/ui/Select';
import { ProjectLifecycleStepper } from '../components/ui/ProjectLifecycleStepper';

interface UseCase {
  projectUseCaseId: number;
  name: string;
  description?: string;
  price: number | string;
  mandatory: boolean;
  implementationStatus?: string;
  selected?: boolean;
  // Sprint number this use case was implemented in (or is being
  // implemented in). Null means it hasn't been picked for any sprint yet.
  sprint?: number | null;
  // Timestamp of the initiate_development call that put this UC into
  // its sprint; same value across every UC in the same sprint.
  sprintStartedAt?: string | null;
}

interface TimelineOption {
  timelineOptionId: number;
  name: string;
  description?: string;
  basePrice?: number | string;
  percentageIncrease?: number | string;
}

interface ChangeRequest {
  changeRequestId: number;
  description: string;
  status: string;
  creationDate: string;
  resolutionMethod?: string;
  screen?: string;
}

interface Incident {
  incidentId: number;
  description: string;
  status: string;
  creationDate: string;
  categorizationType?: string;
}

interface Screen {
  screenId: number;
  name: string;
}

interface ProjectDetails {
  projectId: number;
  name: string;
  description: string;
  status: string;
  creationDate: string;
  previewLink?: string;
  appLink?: string;
  zipFilePath?: string;
  developmentPrice?: number | string;
  totalPrice?: number | string;
  timelineOptionId?: number;
  useCases: UseCase[];
  changeRequests: ChangeRequest[];
  incidents: Incident[];
  screens: Screen[];
  timelineOptions: TimelineOption[];
  userCredits: number;
  // Current implementation sprint. Sprint 1 is the first delivery; the
  // number goes up whenever the user starts another sprint from Delivered.
  currentSprint?: number;
}


function statusBadgeClass(status: string): string {
  switch (status) {
    case 'App preview preparation': return 'bg-blue-600/15 text-blue-700 dark:text-blue-500 border-blue-600/25';
    case 'Rejected': return 'bg-red-600/15 text-red-700 dark:text-red-500 border-red-600/25';
    case 'App preview feedback': return 'bg-amber-600/15 text-amber-700 dark:text-amber-500 border-amber-600/25';
    case 'Technical analysis': return 'bg-cyan-600/15 text-cyan-700 dark:text-cyan-500 border-cyan-600/25';
    case 'Sprint planning': return 'bg-violet-600/15 text-violet-700 dark:text-violet-500 border-violet-600/25';
    case 'For testing': return 'bg-[var(--accent-primary)]/15 text-[var(--accent-primary)] border-[var(--accent-primary)]/25';
    case 'In development': return 'bg-purple-600/15 text-purple-700 dark:text-purple-500 border-purple-600/25';
    case 'Finished testing':
    case 'Delivered': return 'bg-green-600/15 text-green-700 dark:text-green-500 border-green-600/25';
    default: return 'bg-[var(--surface-overlay)] text-[var(--text-secondary)] border-[var(--border-subtle)]';
  }
}

function num(v: number | string | undefined | null): number {
  if (v === undefined || v === null) return 0;
  return typeof v === 'number' ? v : Number(v);
}

function fmtDate(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return iso; }
}

/** Normalize a server enum like 'AppPreviewPreparation' to the spaced display form
 *  'App preview preparation' so our status checks match. Leaves already-spaced
 *  values untouched. */
function normalizeStatus(raw: string | undefined): string {
  if (!raw) return '';
  if (raw.includes(' ')) return raw;
  // Split on capital letters: AppPreviewPreparation -> App Preview Preparation
  const parts = raw.replace(/([a-z])([A-Z])/g, '$1 $2').split(' ');
  if (parts.length === 0) return raw;
  const head = parts[0];
  const tail = parts.slice(1).map((w) => w.toLowerCase());
  return [head, ...tail].join(' ');
}

export function UserProjectDetailWithMandatoryUseCasesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ projectId?: string }>();
  const location = useLocation();

  const projectId = useMemo<number | null>(() => {
    if (params.projectId) return Number(params.projectId);
    const stateId = (location.state as { projectId?: number } | null)?.projectId;
    if (stateId) return stateId;
    const qs = new URLSearchParams(location.search).get('projectId');
    return qs ? Number(qs) : null;
  }, [params.projectId, location.state, location.search]);

  const [project, setProject] = useState<ProjectDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Preparation: selection set of optional use-case ids to buy.
  const [selectedOptional, setSelectedOptional] = useState<Set<number>>(new Set());
  // Feedback: timeline choice.
  const [selectedTimeline, setSelectedTimeline] = useState<number | null>(null);
  // Change request form.
  const [showChangeRequestForm, setShowChangeRequestForm] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState<string>('');
  const [changeDescription, setChangeDescription] = useState<string>('');
  // Incident form.
  const [incidentDetails, setIncidentDetails] = useState<string>('');
  // Change request detail modal.
  const [selectedChangeRequest, setSelectedChangeRequest] = useState<ChangeRequest | null>(null);
  // Initiate development modal.
  const [showInitiateModal, setShowInitiateModal] = useState(false);
  // Finalise app preview modal — moves project to Technical analysis.
  // Surfaces the contract: change requests will be more expensive after.
  const [showFinaliseModal, setShowFinaliseModal] = useState(false);
  // Regeneration notification (dismissible).
  const [showRegenerationNotification, setShowRegenerationNotification] = useState(true);

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (projectId === null) {
      setError('No project ID specified.');
      setIsLoading(false);
      return;
    }
    loadProject(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadProject = async (id: number) => {
    try {
      setIsLoading(true);
      setError('');
      const data = await api.get<ProjectDetails>(`/projects/${id}`);
      data.status = normalizeStatus(data.status);
      setProject(data);
      if (data.timelineOptionId) setSelectedTimeline(data.timelineOptionId);
      // Pre-select optional use-cases already marked selected.
      const preset = new Set<number>();
      (data.useCases ?? []).forEach((u) => {
        if (!u.mandatory && u.selected) preset.add(u.projectUseCaseId);
      });
      setSelectedOptional(preset);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load project';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const mandatoryUseCases = useMemo(
    () => project?.useCases.filter((u) => u.mandatory) ?? [],
    [project]
  );
  const optionalUseCases = useMemo(
    () => project?.useCases.filter((u) => !u.mandatory) ?? [],
    [project]
  );

  const mandatoryTotal = useMemo(
    () => mandatoryUseCases.reduce((acc, u) => acc + num(u.price), 0),
    [mandatoryUseCases]
  );
  // In "preparation" we size cost off the explicit selection set.
  const selectedOptionalTotal = useMemo(
    () => optionalUseCases.filter((u) => selectedOptional.has(u.projectUseCaseId) && u.implementationStatus !== 'Implemented')
      .reduce((acc, u) => acc + num(u.price), 0),
    [optionalUseCases, selectedOptional]
  );
  // In "feedback" the cost reflects both mandatory + selected optional (implemented ones are already sunk).
  const currentProjectPrice = useMemo(
    () => mandatoryTotal + selectedOptionalTotal,
    [mandatoryTotal, selectedOptionalTotal]
  );
  const selectedTimelineOption = useMemo(
    () => project?.timelineOptions.find((t) => t.timelineOptionId === selectedTimeline) ?? null,
    [project, selectedTimeline]
  );
  const finalProjectPrice = useMemo(() => {
    const pct = num(selectedTimelineOption?.percentageIncrease);
    return Math.round(currentProjectPrice * (1 + pct / 100));
  }, [currentProjectPrice, selectedTimelineOption]);

  const openChangeRequests = useMemo(
    () => project?.changeRequests.filter((cr) => cr.status !== 'Resolved' && cr.status !== 'Closed') ?? [],
    [project]
  );

  const screenNames = useMemo<string[]>(
    () => project?.screens.map((s) => s.name).filter(Boolean) ?? [],
    [project]
  );

  const toggleOptional = (id: number, implemented: boolean) => {
    if (implemented) return;
    setSelectedOptional((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirmOptionalSelections = async () => {
    if (!project) return;
    try {
      setIsSaving(true);
      await api.patch(`/projects/${project.projectId}/optional-use-cases`, {
        selectedOptionalUseCaseIds: Array.from(selectedOptional),
      });
      toast.success(t('userProjectDetail.toasts.optionalSaved', 'Optional use cases saved successfully!'));
      await loadProject(project.projectId);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : t('userProjectDetail.toasts.optionalSaveFailed', 'Failed to save optional use cases'));
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDevelopmentInitiation = async () => {
    if (!project || !selectedTimeline) {
      toast.error(t('userProjectDetail.toasts.selectTimelineFirst', 'Select a timeline option first'));
      return;
    }
    try {
      setIsSaving(true);
      await api.post(`/projects/${project.projectId}/initiate-development`, {
        timelineOptionId: selectedTimeline,
        selectedOptionalUseCaseIds: Array.from(selectedOptional),
        creditBalance: project.userCredits,
      });
      toast.success(t('userProjectDetail.toasts.developmentInitiated', 'Development initiated successfully!'));
      setShowInitiateModal(false);
      await loadProject(project.projectId);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : t('userProjectDetail.toasts.initiateFailed', 'Failed to initiate development'));
    } finally {
      setIsSaving(false);
    }
  };

  const submitChangeRequest = async () => {
    if (!project || !changeDescription.trim() || !selectedScreen || project.userCredits < 1) return;
    try {
      setIsSaving(true);
      await api.post(`/projects/${project.projectId}/change-requests`, {
        description: changeDescription.trim(),
        screen: selectedScreen,
      });
      toast.success(t('userProjectDetail.toasts.changeRequestSubmitted', 'Change request submitted successfully!'));
      setSelectedScreen('');
      setChangeDescription('');
      setShowChangeRequestForm(false);
      await loadProject(project.projectId);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : t('userProjectDetail.toasts.changeRequestFailed', 'Failed to submit change request'));
    } finally {
      setIsSaving(false);
    }
  };

  const cancelChangeRequest = () => {
    setSelectedScreen('');
    setChangeDescription('');
    setShowChangeRequestForm(false);
  };

  const submitIncident = async () => {
    if (!project || !incidentDetails.trim()) return;
    try {
      setIsSaving(true);
      await api.post(`/projects/${project.projectId}/incidents`, {
        description: incidentDetails.trim(),
      });
      toast.success(t('userProjectDetail.toasts.incidentSubmitted', 'Incident submitted successfully!'));
      setIncidentDetails('');
      await loadProject(project.projectId);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : t('userProjectDetail.toasts.incidentFailed', 'Failed to submit incident'));
    } finally {
      setIsSaving(false);
    }
  };

  const downloadCode = async () => {
    if (!project) return;
    // window.open cant attach the Bearer token, so fetch the bytes through
    // the api client (auth header + refresh), then trigger the browser
    // download with a temporary blob URL.
    try {
      const blob = await api.get<Blob>(
        `/projects/${project.projectId}/code-download/file`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name || 'project'}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('userProjectDetail.toasts.downloadStarted', 'Code download started!'));
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      toast.error(
        err?.response?.data?.detail ||
          t('userProjectDetail.toasts.downloadFailed', 'Failed to download code'),
      );
    }
  };

  // Kick off a new implementation sprint from a Delivered project. Backend
  // increments current_sprint and flips status back to AppPreviewFeedback;
  // we re-load the project so the existing selection + timeline UI takes over.
  const finaliseAppPreview = async () => {
    if (!project) return;
    setShowFinaliseModal(false);
    try {
      setIsSaving(true);
      await api.post(`/projects/${project.projectId}/finalise-app-preview`);
      toast.success(
        t('userProjectDetail.finaliseAppPreview.success', 'App preview finalised — technical analysis started'),
      );
      await loadProject(project.projectId);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      toast.error(
        err?.response?.data?.detail ||
          t('userProjectDetail.finaliseAppPreview.error', 'Failed to finalise app preview'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const startNextSprint = async () => {
    if (!project) return;
    const ok = window.confirm(
      t(
        'userProjectDetail.nextSprint.confirm',
        'Start a new sprint? The project will move back to feedback so you can pick the next set of use cases to implement.',
      ),
    );
    if (!ok) return;
    try {
      setIsSaving(true);
      await api.post(`/projects/${project.projectId}/next-sprint`);
      toast.success(
        t('userProjectDetail.nextSprint.success', 'Next sprint started'),
      );
      await loadProject(project.projectId);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      toast.error(
        err?.response?.data?.detail ||
          t('userProjectDetail.nextSprint.error', 'Failed to start next sprint'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
        <div className="bg-red-600/15 border border-red-600/25 rounded-xl p-4">
          <p className="text-red-700 text-sm">{error || t('userProjectDetail.notFound', 'Project not found')}</p>
          <button onClick={() => navigate('/user-project-list')}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">
            {t('userProjectDetail.backToProjects', 'Back to projects')}
          </button>
        </div>
      </div>
    );
  }

  const status = project.status;
  const insufficientCreditsForDev = project.userCredits < finalProjectPrice;
  const insufficientCreditsForCR = project.userCredits < 1;
  // Change requests are still allowed in technical analysis / sprint
  // planning — pricing differs but the surface is the same.
  const canCreateChangeRequest =
    status === 'App preview feedback'
    || status === 'Technical analysis'
    || status === 'Sprint planning'
    || status === 'For testing'
    || status === 'In development';

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      {/* Lifecycle stepper — shared component, identical to the admin view. */}
      <ProjectLifecycleStepper status={status} className="mb-6" />

      {/* Hero Header Card */}
      <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm overflow-hidden">
        <div className="h-1.5 bg-gradient-to-r from-[var(--accent-primary)] to-blue-400"></div>
        <div className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)] break-words">{project.name}</h1>
              <ExpandableMarkdown
                text={project.description || ''}
                className="mt-2 text-base sm:text-sm text-[var(--text-secondary)]"
              />
              <div className="mt-3">
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${statusBadgeClass(status)}`}>
                  {status}
                </span>
              </div>
            </div>
            {/* Hero action links. Preview is visible from App preview feedback
                onwards through Delivered — once the admin publishes the
                preview link it must remain reachable for the user in every
                subsequent state (technical analysis, sprint planning,
                development, testing, delivery). Open App is gated to
                testing/delivery states where a built app actually exists.
                `previewLink` / `appLink` falsy filters prevent dead buttons
                when the admin hasn't set the link yet. */}
            {((['App preview feedback', 'Technical analysis', 'Sprint planning', 'In development', 'For testing', 'Finished testing', 'Delivered'].includes(status))
              && (project.previewLink || project.appLink)) && (
              <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2">
                {project.previewLink && (
                  <a href={project.previewLink} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors">
                    {t('userProjectDetail.viewPreview', 'View App Preview')}
                    <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
                {['For testing', 'Finished testing', 'Delivered'].includes(status) && project.appLink && (
                  <a href={project.appLink} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors shadow-sm">
                    {t('userProjectDetail.openApp', 'Open App')}
                    <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mandatory Use Cases — hidden during App preview preparation (the
          admin is still scoping the project; use-case / timeline detail
          belongs to later phases). */}
      {status !== 'App preview preparation' && (
      <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
        <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">
          <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-600/10">
            <svg className="h-4 w-4 text-[var(--accent-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          {t('userProjectDetail.mandatoryTitle', 'Mandatory Use Cases')}
        </h2>
        {mandatoryUseCases.length === 0 && (
          <p className="text-sm text-[var(--text-muted)]">{t('userProjectDetail.noMandatory', 'No mandatory use cases for this project.')}</p>
        )}
        <div className="space-y-3">
          {mandatoryUseCases.map((u) => (
            <div key={u.projectUseCaseId}
              className="flex items-center gap-3 p-3 rounded-lg bg-[var(--surface-raised)] border border-[var(--border-subtle)]">
              <div className="h-5 w-5 rounded flex items-center justify-center border-2 flex-shrink-0 bg-[var(--surface-overlay)] border-[var(--border-default)] opacity-50">
                <svg className="h-3 w-3 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base sm:text-sm font-medium text-[var(--text-primary)]">{u.name}</div>
                {u.description && (
                  <div className="text-sm text-[var(--text-secondary)] mt-0.5">{u.description}</div>
                )}
              </div>
              <div className="text-base sm:text-sm font-semibold text-[var(--text-primary)] text-right">{fmtMoney(u.price, 0)}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-[var(--border-subtle)]">
          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/10">
            <div className="text-sm font-medium text-[var(--text-secondary)]">{t('userProjectDetail.mandatoryTotal', 'Mandatory Use Cases Total')}</div>
            <div className="text-lg font-bold text-[var(--text-primary)]">{fmtMoney(mandatoryTotal, 0)}</div>
          </div>
        </div>
      </div>
      )}

      {/* Sprint progress — visible in every state past preparation. Groups use
          cases by their sprint number. Each row carries a status derived from
          (use case implementationStatus, project.status, uc.sprint == current
          sprint) so the user always knows whether a given use case is live,
          being built, in testing, or ready to ship. Rows without a sprint
          assignment don't appear here — they're either backlog (shown in the
          Delivered card) or the old pre-sprint era that never got stamped. */}
      {status !== 'App preview preparation' && (() => {
        const useCases = project.useCases || [];
        const stamped = useCases.filter(u => typeof u.sprint === 'number' && u.sprint !== null);
        if (stamped.length === 0) return null;
        const currentSprint = project.currentSprint ?? 1;
        // Bucket by sprint, oldest first so the history reads top-to-bottom.
        const bySprint = new Map<number, UseCase[]>();
        for (const uc of stamped) {
          const s = uc.sprint as number;
          if (!bySprint.has(s)) bySprint.set(s, []);
          bySprint.get(s)!.push(uc);
        }
        const sortedSprints = Array.from(bySprint.keys()).sort((a, b) => a - b);
        const selectedTimeline = project.timelineOptions?.find(
          (opt) => opt.timelineOptionId === project.timelineOptionId
        );
        const timelinePct = selectedTimeline ? Math.round(num(selectedTimeline.percentageIncrease)) : null;

        const useCaseStatus = (uc: UseCase): { label: string; tone: 'done' | 'active' | 'pending' } => {
          if (uc.implementationStatus === 'Implemented') {
            return { label: t('userProjectDetail.sprintStatus.delivered', 'Delivered'), tone: 'done' };
          }
          if (uc.sprint === currentSprint) {
            if (status === 'In development')
              return { label: t('userProjectDetail.sprintStatus.inDevelopment', 'In development'), tone: 'active' };
            if (status === 'For testing')
              return { label: t('userProjectDetail.sprintStatus.inTesting', 'In testing'), tone: 'active' };
            if (status === 'Finished testing')
              return { label: t('userProjectDetail.sprintStatus.testingComplete', 'Testing complete'), tone: 'active' };
            return { label: t('userProjectDetail.sprintStatus.scheduled', 'Scheduled'), tone: 'pending' };
          }
          return { label: t('userProjectDetail.sprintStatus.pending', 'Pending'), tone: 'pending' };
        };

        const toneClass = (tone: 'done' | 'active' | 'pending') =>
          tone === 'done'
            ? 'bg-green-600/15 text-green-700 dark:text-green-500 border-green-600/25'
            : tone === 'active'
              ? 'bg-[var(--accent-primary)]/15 text-[var(--accent-primary)] border-[var(--accent-primary)]/25'
              : 'bg-[var(--surface-overlay)] text-[var(--text-secondary)] border-[var(--border-subtle)]';

        return (
          <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-[var(--text-primary)]">
                <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-[var(--accent-primary)]/10">
                  <svg className="h-4 w-4 text-[var(--accent-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </span>
                {t('userProjectDetail.sprintProgressTitle', 'Sprint progress')}
              </h2>
              {selectedTimeline && (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-600/15 text-amber-700 dark:text-amber-500 border border-amber-600/25"
                  title={selectedTimeline.description || selectedTimeline.name || ''}
                >
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {selectedTimeline.name || t('userProjectDetail.timelineFallback', 'Timeline')}
                  {timelinePct !== null && timelinePct !== 0 && (
                    <span className="font-bold">+{timelinePct}%</span>
                  )}
                </span>
              )}
            </div>
            <div className="space-y-5">
              {sortedSprints.map(s => {
                const isCurrent = s === currentSprint;
                const startedAtRaw = bySprint.get(s)!
                  .map(uc => uc.sprintStartedAt)
                  .find(v => !!v);
                let startedAtLabel = '';
                if (startedAtRaw) {
                  try {
                    startedAtLabel = new Date(startedAtRaw).toLocaleString();
                  } catch {
                    startedAtLabel = String(startedAtRaw).slice(0, 16).replace('T', ' ');
                  }
                }
                return (
                  <div key={s}>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                        isCurrent
                          ? 'bg-[var(--accent-primary)]/15 text-[var(--accent-primary)] border-[var(--accent-primary)]/25'
                          : 'bg-[var(--surface-raised)] text-[var(--text-secondary)] border-[var(--border-subtle)]'
                      }`}>
                        {t('userProjectDetail.sprintLabel', 'Sprint {{n}}').replace('{{n}}', String(s))}
                      </span>
                      <span className="text-xs text-[var(--text-muted)]">
                        {bySprint.get(s)!.length} {t('userProjectDetail.sprintUseCaseCount', 'use case(s)')}
                      </span>
                      {startedAtLabel && (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-[var(--text-muted)]"
                          title={t('userProjectDetail.sprintInitiatedHint', 'When development was initiated for this sprint')}
                        >
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {t('userProjectDetail.sprintInitiatedAt', 'Initiated {{when}}').replace('{{when}}', startedAtLabel)}
                        </span>
                      )}
                    </div>
                    <ul className="space-y-1.5 pl-1">
                      {bySprint.get(s)!.map(uc => {
                        const ucs = useCaseStatus(uc);
                        return (
                          <li key={uc.projectUseCaseId} className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                            {ucs.tone === 'done' ? (
                              <svg className="h-4 w-4 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            ) : ucs.tone === 'active' ? (
                              <svg className="h-4 w-4 text-[var(--accent-primary)] flex-shrink-0 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            ) : (
                              <div className="h-4 w-4 rounded-full border-2 border-[var(--border-default)] flex-shrink-0" />
                            )}
                            <span className="flex-1">{uc.name}</span>
                            {uc.mandatory && (
                              <span className="text-[10px] uppercase font-semibold text-[var(--text-muted)]">
                                {t('userProjectDetail.mandatoryTag', 'Mandatory')}
                              </span>
                            )}
                            <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${toneClass(ucs.tone)}`}>
                              {ucs.label}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* App preview feedback — user reviews preview, files change requests,
          and when satisfied clicks "App preview finalised" to move the
          project into Technical analysis. Modal restates the contract:
          after this step, change requests are more expensive. */}
      {status === 'App preview feedback' && (
        <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
          <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-2">
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-600/10">
              <svg className="h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </span>
            {t('userProjectDetail.finaliseAppPreview.title', 'Happy with the app preview?')}
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            {t(
              'userProjectDetail.finaliseAppPreview.body',
              "When you're done iterating on the preview via change requests, finalise it to start technical analysis. We'll scope the use cases, set their prices, and you'll then pick what to ship in your first sprint.",
            )}
          </p>
          <button
            type="button"
            onClick={() => setShowFinaliseModal(true)}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--accent-primary)] hover:bg-[var(--accent-secondary)] text-[var(--accent-text)] rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {t('userProjectDetail.finaliseAppPreview.button', 'App preview finalised')}
          </button>
        </div>
      )}

      {/* Technical analysis — read-only state. Admin is scoping use cases
          and pricing on their side; user just waits and (optionally) keeps
          filing change requests. */}
      {status === 'Technical analysis' && (
        <div className="mb-6 bg-cyan-50 dark:bg-cyan-900/15 rounded-xl border border-cyan-200 dark:border-cyan-700/40 p-4 sm:p-5">
          <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-cyan-900 dark:text-cyan-100 mb-1">
            <svg className="h-5 w-5 text-cyan-600 dark:text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            {t('userProjectDetail.technicalAnalysis.title', "We're working on the technical analysis")}
          </h2>
          <p className="text-sm text-cyan-900 dark:text-cyan-100">
            {t(
              'userProjectDetail.technicalAnalysis.body',
              "Our team is scoping use cases and setting prices for your project. Once they're done, you'll get an email and the project will move to sprint planning so you can choose what to ship first.",
            )}
          </p>
        </div>
      )}

      {/* Sprint planning — admin finished technical analysis. User now
          picks optional use cases + timeline and clicks Initiate Development
          to enter the existing development flow. */}
      {status === 'Sprint planning' && (
        <div className="space-y-6">
          {optionalUseCases.length > 0 && (
          <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
            <div className="space-y-3">
              <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-[var(--text-primary)]">
                <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-purple-600/10">
                  <svg className="h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </span>
                {t('userProjectDetail.optionalTitle', 'Optional Use Cases')}
              </h2>
              {optionalUseCases.map((u) => {
                const implemented = u.implementationStatus === 'Implemented';
                const selected = selectedOptional.has(u.projectUseCaseId);
                return (
                  <div key={u.projectUseCaseId}
                    onClick={() => toggleOptional(u.projectUseCaseId, implemented)}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                      implemented ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-[var(--surface-raised)] active:scale-[0.99]'
                    }`}>
                    <div className={`h-5 w-5 rounded flex items-center justify-center border-2 flex-shrink-0 transition-colors ${
                      implemented ? 'bg-[var(--surface-overlay)] border-[var(--border-default)] opacity-50' :
                      selected ? 'bg-[var(--accent-primary)] border-[var(--accent-primary)]' :
                      'bg-[var(--surface-raised)] border-[var(--border-default)]'
                    }`}>
                      {(selected || implemented) && (
                        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-base sm:text-sm font-medium text-[var(--text-primary)]">{u.name}</div>
                    </div>
                    <div className="text-base sm:text-sm font-semibold text-[var(--text-primary)] text-right">{fmtMoney(u.price, 0)}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-3 border-t border-[var(--border-subtle)]">
              <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/10">
                <div className="text-sm font-medium text-[var(--text-secondary)]">{t('userProjectDetail.priceWithSelected', 'Price with selected use cases')}</div>
                <div className="text-lg font-bold text-[var(--text-primary)]">{fmtMoney(currentProjectPrice, 0)}</div>
              </div>
            </div>
          </div>
          )}

          {/* Timeline Selection */}
          <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
            <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-600/10">
                <svg className="h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              {t('userProjectDetail.selectTimeline', 'Select Timeline')}
            </h2>
            <div className="space-y-3">
              {project.timelineOptions.map((opt) => {
                const isSelected = selectedTimeline === opt.timelineOptionId;
                const pct = num(opt.percentageIncrease);
                const extra = Math.round(currentProjectPrice * (pct / 100));
                return (
                  <div key={opt.timelineOptionId}
                    onClick={() => setSelectedTimeline(opt.timelineOptionId)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      isSelected ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)]/5 shadow-sm' :
                      'border-[var(--border-subtle)] hover:border-[var(--border-default)] hover:bg-[var(--surface-raised)]'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="text-base sm:text-sm font-medium text-[var(--text-primary)]">{opt.name}</div>
                        {opt.description && (
                          <div className="text-sm text-[var(--text-secondary)] mt-1">{opt.description}</div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0 pl-3">
                        <div className="text-base sm:text-sm font-semibold text-[var(--text-primary)]">+{pct}%</div>
                        <div className="text-sm text-[var(--text-secondary)]">{fmtMoney(extra)} {t('userProjectDetail.extra', 'extra')}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedTimeline && (
              <div className="mt-5 pt-4 border-t border-[var(--border-subtle)]">
                <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/10">
                  <span className="text-sm font-medium text-[var(--text-secondary)]">{t('userProjectDetail.finalPrice', 'Final Project Price')}</span>
                  <span className="text-xl font-bold text-[var(--text-primary)]">{fmtMoney(finalProjectPrice, 0)}</span>
                </div>
              </div>
            )}

            <div className="mt-5 sm:mt-6">
              {insufficientCreditsForDev && selectedTimeline && (
                <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-xl mb-4">
                  <svg className="h-5 w-5 mt-0.5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-amber-800 dark:text-amber-400 mb-1">{t('userProjectDetail.insufficientDev', 'Insufficient credits for development')}</div>
                    <div className="text-sm text-amber-700 dark:text-amber-500">
                      {t('userProjectDetail.insufficientDevBody', 'You need {{need}} but currently have {{have}}.', { need: fmtMoney(finalProjectPrice), have: fmtMoney(project.userCredits) })}
                    </div>
                    <div className="mt-2 flex justify-end">
                      <button onClick={() => navigate('/user-credit-purchase')}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-amber-100 dark:bg-amber-800/30 text-amber-800 dark:text-amber-400 hover:bg-amber-200 border border-amber-300 dark:border-amber-700/50 transition-colors">
                        {t('userProjectDetail.purchaseCredits', 'Purchase € Credits')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <button onClick={() => setShowInitiateModal(true)}
                  disabled={!selectedTimeline || insufficientCreditsForDev}
                  className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-sm font-medium transition-colors shadow-sm">
                  {t('userProjectDetail.initiateDevelopment', 'Initiate Development')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Change Request button */}
      {canCreateChangeRequest && (
        <div className="mt-8 mb-6 flex justify-end">
          <button onClick={() => setShowChangeRequestForm((v) => !v)}
            className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors shadow-sm">
            {t('userProjectDetail.createChangeRequest', 'Create Change Request')}
          </button>
        </div>
      )}

      {/* Change Request Form */}
      {showChangeRequestForm && (
        <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
          <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-amber-600/10">
              <svg className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </span>
            {t('userProjectDetail.submitChangeRequest', 'Submit Change Request')}
          </h2>
          <div className="mb-4">
            <Select<string>
              label={t('userProjectDetail.selectScreen', 'Select Screen')}
              placeholder={t('userProjectDetail.chooseScreen', 'Choose a screen...')}
              value={selectedScreen}
              onChange={setSelectedScreen}
              options={
                screenNames.length === 0
                  ? [{ value: '__any__', label: t('userProjectDetail.noScreens', '(No screens available)') }]
                  : screenNames.map((s) => ({ value: s, label: s }))
              }
            />
          </div>
          {selectedScreen && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">{t('userProjectDetail.descriptionOfChanges', 'Description of Desired Changes')}</label>
                <textarea value={changeDescription} onChange={(e) => setChangeDescription(e.target.value)} rows={4}
                  placeholder={t('userProjectDetail.describeChanges', "Describe the changes you'd like to see...")}
                  className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-base sm:text-sm resize-none"></textarea>
              </div>
              {insufficientCreditsForCR && (
                <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-xl">
                  <svg className="h-5 w-5 mt-0.5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-amber-800 dark:text-amber-400 mb-1">{t('userProjectDetail.insufficientCredits', 'Insufficient credits')}</div>
                    <div className="text-sm text-amber-700 dark:text-amber-500">
                      {t('userProjectDetail.insufficientCRBody', 'You need at least €1 to submit a change request.')}
                    </div>
                    <div className="mt-2 flex justify-end">
                      <button onClick={() => navigate('/user-credit-purchase')}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-amber-100 dark:bg-amber-800/30 text-amber-800 dark:text-amber-400 hover:bg-amber-200 border border-amber-300 dark:border-amber-700/50 transition-colors">
                        Purchase € Credits
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button onClick={cancelChangeRequest} type="button"
                  className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors">
                  {t('common.cancel', 'Cancel')}
                </button>
                <button onClick={submitChangeRequest} type="button"
                  disabled={isSaving || !changeDescription.trim() || insufficientCreditsForCR}
                  className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-sm font-medium transition-colors shadow-sm">
                  {t('userProjectDetail.submitCRButton', 'Submit Change Request (€1)')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Open Change Requests */}
      {openChangeRequests.length > 0 && (
        <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
          <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">
            <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-amber-600/10">
              <svg className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </span>
            {t('userProjectDetail.openChangeRequests', 'Your Open Change Requests')}
          </h2>
          <div className="space-y-3">
            {openChangeRequests.map((cr) => (
              <div key={cr.changeRequestId}
                onClick={() => setSelectedChangeRequest(cr)}
                className="bg-[var(--surface-raised)] rounded-xl border border-[var(--border-subtle)] p-4 cursor-pointer hover:bg-[var(--surface-overlay)] hover:border-[var(--border-default)] hover:shadow-sm active:scale-[0.99] transition-all">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    {cr.screen && (
                      <div className="text-base sm:text-sm font-medium text-[var(--text-primary)]">{cr.screen}</div>
                    )}
                    <div className="text-sm text-[var(--text-secondary)] mt-1 line-clamp-2">{cr.description}</div>
                    <div className="text-xs text-[var(--text-muted)] mt-2">{fmtDate(cr.creationDate)}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="px-2.5 py-1 text-xs rounded-full bg-amber-600/15 text-amber-700 dark:text-amber-500 border border-amber-600/25">{cr.status}</span>
                    <svg className="h-5 w-5 text-[var(--text-muted)]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* For Testing — Incident submission + list + regeneration notice */}
      {status === 'For testing' && (
        <div className="space-y-6">
          <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
            <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-600/10">
                <svg className="h-4 w-4 text-[var(--accent-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </span>
              {t('userProjectDetail.testingInterface', 'Testing Interface')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">{t('userProjectDetail.incidentDetails', 'Incident Details')}</label>
                <textarea value={incidentDetails} onChange={(e) => setIncidentDetails(e.target.value)} rows={4}
                  placeholder={t('userProjectDetail.describeIssue', 'Describe the issue you encountered...')}
                  className="w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-base sm:text-sm resize-none"></textarea>
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button onClick={() => setIncidentDetails('')} type="button"
                  className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors">
                  {t('common.cancel', 'Cancel')}
                </button>
                <button onClick={submitIncident} type="button"
                  disabled={isSaving || !incidentDetails.trim()}
                  className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-sm font-medium transition-colors shadow-sm">
                  {t('userProjectDetail.submitIncident', 'Submit Incident')}
                </button>
              </div>
            </div>
          </div>

          {project.incidents.length > 0 && (
            <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
              <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">
                <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-red-600/10">
                  <svg className="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                {t('userProjectDetail.incidentHistory', 'Incident History')}
              </h2>
              <div className="space-y-3">
                {project.incidents.map((inc) => (
                  <div key={inc.incidentId} className="bg-[var(--surface-raised)] rounded-xl border border-[var(--border-subtle)] p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-[var(--text-secondary)] mb-1">{fmtDate(inc.creationDate)}</div>
                        <div className="text-base sm:text-sm text-[var(--text-primary)] break-words">{inc.description}</div>
                      </div>
                      <span className={`px-2.5 py-1 text-xs rounded-full border flex-shrink-0 ${
                        inc.status === 'Open' ? 'bg-amber-600/15 text-amber-700 dark:text-amber-500 border-amber-600/25' :
                        inc.status === 'Resolved' ? 'bg-green-600/15 text-green-700 dark:text-green-500 border-green-600/25' :
                        'bg-[var(--surface-overlay)] text-[var(--text-secondary)] border-[var(--border-subtle)]'
                      }`}>{inc.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showRegenerationNotification && project.changeRequests.some((cr) => cr.resolutionMethod === 'Regeneration') && (
            <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
              <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">
                <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-blue-600/10">
                  <svg className="h-4 w-4 text-[var(--accent-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </span>
                {t('userProjectDetail.crResolution', 'Change Request Resolution')}
              </h2>
              <div className="p-4 bg-blue-600/15 border border-blue-600/25 rounded-xl mb-4">
                <div className="text-sm text-blue-700 dark:text-blue-500">
                  {t('userProjectDetail.crResolutionBody', 'An incident has been categorized as a change request. You can resolve it via regeneration.')}
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button onClick={() => setShowRegenerationNotification(false)} type="button"
                  className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors">
                  {t('userProjectDetail.decline', 'Decline')}
                </button>
                <button onClick={() => navigate('/user-change-request-regeneration', { state: { projectId: project.projectId } })} type="button"
                  className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors shadow-sm">
                  {t('userProjectDetail.resolveViaRegen', 'Resolve via Regeneration')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* In Development */}
      {status === 'In development' && (
        <div className="space-y-6">
          <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
            <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-purple-600/10">
                <svg className="h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </span>
              {t('userProjectDetail.devInProgress', 'Development in Progress')}
            </h2>
            <div className="p-4 bg-blue-600/15 border border-blue-600/25 rounded-xl">
              <div className="text-sm text-blue-700 dark:text-blue-500">
                {t('userProjectDetail.devInProgressBody', "Your project is currently being developed. You will be notified when it's ready for testing.")}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delivered */}
      {status === 'Delivered' && (() => {
        // Backlog = optional use cases not yet implemented; eligible picks
        // for the next sprint.
        const unimplementedOptional = (project.useCases || []).filter(
          u => !u.mandatory && u.implementationStatus !== 'Implemented'
        );
        return (
        <div className="space-y-6">
          <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
            <h2 className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-4">
              <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-green-600/10">
                <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              {t('userProjectDetail.delivered', 'Project Delivered')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <div className="text-sm font-medium text-[var(--text-secondary)] mb-1">{t('userProjectDetail.completionDate', 'Completion Date')}</div>
                <div className="text-base sm:text-sm text-[var(--text-primary)]">{fmtDate(project.creationDate)}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-[var(--text-secondary)] mb-1">{t('userProjectDetail.finalPriceLabel', 'Final Price')}</div>
                <div className="text-base sm:text-sm text-[var(--text-primary)]">{fmtMoney(project.totalPrice, 0)}</div>
              </div>
            </div>
            <div className="pt-4 border-t border-[var(--border-subtle)] flex justify-end">
              <button onClick={downloadCode} type="button"
                className="inline-flex items-center px-4 py-2 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors shadow-sm">
                <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {t('userProjectDetail.downloadZip', 'Download Code ZIP')}
              </button>
            </div>
          </div>

          {/* Backlog — optional use cases not yet implemented. */}
          <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-4 sm:p-5 lg:p-6">
            <h3 className="text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-2">
              {t('userProjectDetail.backlogTitle', 'Ready for the next sprint')}
            </h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              {t(
                'userProjectDetail.backlogSubtitle',
                'Optional use cases that are not yet implemented. Start a new sprint to pick which ones to build next.',
              )}
            </p>
            {unimplementedOptional.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)]">
                {t('userProjectDetail.backlogEmpty', 'Every optional use case has been implemented.')}
              </p>
            ) : (
              <ul className="space-y-2">
                {unimplementedOptional.map(uc => (
                  <li key={uc.projectUseCaseId}
                    className="flex items-center gap-3 p-3 rounded-lg bg-[var(--surface-raised)] border border-[var(--border-subtle)]">
                    {/* Bullet dot — purely informational; selection happens
                        in the feedback-phase picker after "Start next sprint". */}
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--text-muted)] flex-shrink-0" aria-hidden />
                    <div className="flex-1 min-w-0">
                      <div className="text-base sm:text-sm font-medium text-[var(--text-primary)]">{uc.name}</div>
                    </div>
                    <div className="text-base sm:text-sm font-semibold text-[var(--text-primary)] text-right">{fmtMoney(uc.price, 0)}</div>
                  </li>
                ))}
              </ul>
            )}
            {unimplementedOptional.length > 0 && (
              <div className="mt-5 pt-4 border-t border-[var(--border-subtle)] flex justify-end">
                <button
                  onClick={startNextSprint}
                  disabled={isSaving}
                  type="button"
                  className="inline-flex items-center gap-2 px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-sm font-medium transition-colors shadow-sm"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 12h15" />
                  </svg>
                  {t('userProjectDetail.nextSprint.action', 'Start next sprint')}
                </button>
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {/* Change Request Detail Modal */}
      {selectedChangeRequest && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-[var(--surface-raised)] rounded-2xl border border-[var(--border-subtle)] p-5 sm:p-6 max-w-lg w-full shadow-2xl mx-4">
            <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">{t('userProjectDetail.crModalTitle', 'Change Request Details')}</h2>
            <div className="space-y-4">
              {selectedChangeRequest.screen && (
                <div>
                  <div className="text-sm font-medium text-[var(--text-secondary)] mb-1">{t('userProjectDetail.screenLabel', 'Screen')}</div>
                  <div className="text-base text-[var(--text-primary)]">{selectedChangeRequest.screen}</div>
                </div>
              )}
              <div>
                <div className="text-sm font-medium text-[var(--text-secondary)] mb-1">{t('userProjectDetail.descriptionLabel', 'Description')}</div>
                <div className="text-base text-[var(--text-primary)] whitespace-pre-wrap break-words">{selectedChangeRequest.description}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-[var(--text-secondary)] mb-1">{t('userProjectDetail.statusLabel', 'Status')}</div>
                <span className="px-2.5 py-1 text-xs rounded-full bg-amber-600/15 text-amber-700 dark:text-amber-500 border border-amber-600/25">
                  {selectedChangeRequest.status}
                </span>
              </div>
              <div>
                <div className="text-sm font-medium text-[var(--text-secondary)] mb-1">{t('userProjectDetail.createdDate', 'Created Date')}</div>
                <div className="text-base text-[var(--text-primary)]">{fmtDate(selectedChangeRequest.creationDate)}</div>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => setSelectedChangeRequest(null)}
                className="px-5 py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-sm font-medium transition-colors shadow-sm">
                {t('userProjectDetail.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* App preview finalised modal — gates AppPreviewFeedback → TechnicalAnalysis. */}
      {showFinaliseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-[var(--surface-raised)] rounded-xl border border-[var(--border-subtle)] p-5 sm:p-6 max-w-lg w-full shadow-2xl">
            <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-3">
              {t('userProjectDetail.finaliseAppPreview.modalTitle', 'Finalise app preview?')}
            </h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              {t(
                'userProjectDetail.finaliseAppPreview.modalBody',
                "We'll move the project to Technical analysis. Our team will scope use cases and set their prices, then you'll pick what to ship in your first sprint.",
              )}
            </p>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-lg p-3 mb-5 flex items-start gap-2">
              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm text-amber-900 dark:text-amber-100">
                {t(
                  'userProjectDetail.finaliseAppPreview.warning',
                  'After this step, change requests will be more expensive — they enter our paid technical-analysis pricing tier.',
                )}
              </p>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowFinaliseModal(false)}
                disabled={isSaving}
                className="px-4 py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-sm font-medium disabled:opacity-50"
              >
                {t('userProjectDetail.finaliseAppPreview.back', 'Keep iterating')}
              </button>
              <button
                type="button"
                onClick={finaliseAppPreview}
                disabled={isSaving}
                className="px-4 py-2.5 bg-[var(--accent-primary)] hover:bg-[var(--accent-secondary)] text-[var(--accent-text)] rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {isSaving
                  ? t('userProjectDetail.finaliseAppPreview.confirming', 'Finalising...')
                  : t('userProjectDetail.finaliseAppPreview.confirm', 'Yes, start technical analysis')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Initiate Development Modal */}
      {showInitiateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-[var(--surface-raised)] rounded-2xl border border-[var(--border-subtle)] p-5 sm:p-6 max-w-sm sm:max-w-md w-full shadow-2xl mx-4">
            <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-4">{t('userProjectDetail.confirmInitiation', 'Confirm Development Initiation')}</h2>
            <div className="mb-6 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/10">
              <div className="text-sm text-[var(--text-secondary)] mb-1">{t('userProjectDetail.finalPrice', 'Final Project Price')}</div>
              <div className="text-2xl font-bold text-[var(--text-primary)]">{fmtMoney(finalProjectPrice, 0)}</div>
              <div className="text-sm text-[var(--text-secondary)] mt-1">{t('userProjectDetail.timelineLabel', 'Timeline')}: {selectedTimelineOption?.name ?? '—'}</div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button onClick={() => setShowInitiateModal(false)}
                className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors">
                Cancel
              </button>
              <button onClick={confirmDevelopmentInitiation} disabled={isSaving}
                className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-base sm:text-sm font-medium transition-colors shadow-sm disabled:opacity-50">
                {isSaving ? t('userProjectDetail.processing', 'Processing…') : t('userProjectDetail.confirmInitiationButton', 'Confirm Initiation')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserProjectDetailWithMandatoryUseCasesPage;
